import { Form } from 'antd';
import React, { useState } from 'react';
import Button from '../../../components/Button/Button';
import FormItem from '../../../components/FormItem/FormItem';
import Input from '../../../components/Input/Input';
import { Login as LoginPayload } from '../../../models/auth';
import { DEFAULT_ERROR_MESSAGE } from '../../../models/common/async-result';
import { performLogin } from '../../../service/auth';
import { createErrorNotification } from '../../../service/notification';
import { useNavigate  } from 'react-router-dom';

import { useUserStore } from '../../../store/useUser';
import { getAllUserClubs } from '../../../service/clubUser';
import { useSidebarStore } from '../../../store/useSidebar';
import useHeader from '../../../hooks/useHeader';
import SelectClubProfile from '../../../container/Modal/Modals/SelectClubProfile/SelectClubProfile';
import Modal from '../../../components/Modal/Modal';

import styles from './Login.module.scss';

const Login: React.FC = () => {
    const [form] = Form.useForm<LoginPayload>();

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);

    const setJwt = useUserStore((state) => state.setJwt);

    const setUserId = useUserStore((state) => state.setUserId);
    const setProfile = useUserStore((state) => state.setProfile);
    const setClubs = useUserStore((state) => state.setClubs);
    const setMenu = useSidebarStore(state => state.setMenu)
    
    const clubs = useUserStore((state) => state.clubs);
    const clubsWhereUserIsManager = (clubs || []).filter(club => club.isCreator || club.isMediator)

    useHeader({ header: 'Login' });

    const onFinish = async (): Promise<Boolean> => {
        try {
            if(isLoading) return false;

            const payload = form.getFieldsValue()

            setIsLoading(true);

            const {error, success: jwt} = await performLogin(payload);

            if(error) {
                createErrorNotification(error || DEFAULT_ERROR_MESSAGE);
                return false;
            }

            if(!jwt) {
                createErrorNotification(error || DEFAULT_ERROR_MESSAGE);
                return false;
            }

            const clubs = await getAllUserClubs(jwt.user);

            setClubs(clubs);
            setJwt(jwt)
            setUserId(jwt.user);

            const userIsManager = clubs.filter(club => club.isCreator || club.isMediator).length > 0

            if(!userIsManager) {
               setProfile('Clubista')
               setMenu();
               navigate(`/user/${jwt.user}`, { replace:true });
               return false;
            }

            return true;
        } catch (error) {
            createErrorNotification(DEFAULT_ERROR_MESSAGE);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    const handleForgotPassword = () => navigate(`/reset-password`, { replace: true })

    return (
        <div className={styles.container}>
            <div className={styles.maskContainer}></div>
            <div className={styles.authenticationContainer}>
                <div className={styles.authenticationForm}>
                    <div className={styles.image}>
                        <img src='./assets/MF-p fundo claro@2x.png' alt="logo clube de leitura"></img>
                    </div>
                    <Form 
                        layout="vertical"
                        form={form}
                        id="formLogin" 
                        className={styles.FormContainer}>
                        <h1 className={styles.title}>Entrar</h1>
                        <FormItem
                            label="Email"
                            name="email"
                            style={styles.username}
                            rules={[{required: true, message: 'Por favor, insira seu email!'}]}
                            >
                            <Input placeholder="Email" type="email"/>
                        </FormItem>
                        <FormItem
                            label="Senha"
                            name="password"
                            rules={[{required: true, message: 'Por favor, insira sua senha!'}]}
                            >
                            <Input placeholder="Senha" type="password"/>
                        </FormItem>
                        <Modal
                            renderToggle={({ setOpen })=> {
                                return (
                                    <Button
                                        submit
                                        loading={isLoading}
                                        disabled={isLoading}
                                        style={styles.loginButton}
                                        onClick={async () => {
                                            const success = await onFinish();
                                            if(success){
                                                setOpen(true);
                                            }
                                        }}
                                    >
                                        Entrar
                                    </Button>
                                )
                            }}
                        >
                            {() => <SelectClubProfile clubs={clubsWhereUserIsManager} />}
                        </Modal>
                        <Button 
                            type='link' 
                            loading={isLoading} 
                            disabled={isLoading} 
                            onClick={handleForgotPassword}
                            style={styles.forgotPassButton}
                            >Esqueceu a senha?
                        </Button>
                    </Form>
                    <div className={styles.redirectLink}>
                        Ainda não tem conta? <a href="/register" className={styles.link}>Registrar.</a>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Login;